import { useLayoutEffect, useMemo, useRef } from 'react';
import { AssetRef, Resource } from '../../../types';
import { assignTypedTokens, mediaKindOfRef, mediaKindOfResource } from '../../../utils/assetTokens';
import { useAtMention, MentionItem } from './useAtMention';
import AssetMentionMenu from './AssetMentionMenu';
import { getCaretCoordinates } from '../../../utils/getCaretCoordinates';
import { ShotEditFormData } from '../types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ShotPromptEditorProps {
  shotEdits: ShotEditFormData;
  onShotEditsChange: React.Dispatch<React.SetStateAction<ShotEditFormData>>;
  refs: AssetRef[];
  resources: Resource[];
  t: Translate;
}

const getShotRefResourceLabel = (resource: Resource, t: Translate) => {
  if (resource.source !== 'shot_ref') return resource.fileName;
  return resource.type === 'image' ? t('shot.refImage') : t('shot.refVideo');
};

export default function ShotPromptEditor({ shotEdits, onShotEditsChange, refs, resources, t }: ShotPromptEditorProps) {
  const refByIdx = new Map(refs.map(r => [r.imgIndex, r]));
  const resById = new Map(resources.map(r => [r.id, r]));

  const mentionItems: MentionItem[] = useMemo(() => assignTypedTokens(refs, resources).flatMap(it => {
    if (it.kind === 'ref' && it.imgIndex != null) {
      const ref = refByIdx.get(it.imgIndex);
      if (!ref) return [];
      return [{
        id: `ref:${it.imgIndex}`, token: it.token,
        kind: mediaKindOfRef(ref) === 'video' ? 'video' : 'image',
        thumbnailUrl: ref.imageUrl, label: ref.name,
      } as MentionItem];
    }
    if (it.kind === 'res' && it.resId) {
      const res = resById.get(it.resId);
      if (!res) return [];
      return [{
        id: `res:${res.id}`, token: it.token,
        kind: mediaKindOfResource(res), thumbnailUrl: res.url,
        label: getShotRefResourceLabel(res, t),
      } as MentionItem];
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [refs, resources]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autoResize = (el: HTMLTextAreaElement) => { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; };
  useLayoutEffect(() => {
    if (textareaRef.current) autoResize(textareaRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shotEdits.prompt]);

  const mention = useAtMention({
    items: mentionItems,
    value: shotEdits.prompt,
    onChange: (next) => onShotEditsChange({ ...shotEdits, prompt: next }),
    textareaRef,
  });

  const MENTION_ROW_PX = 36;
  const MENTION_MENU_MAX_PX = 220;
  let menuCoords: { top: number; left: number } | null = null;
  if (mention.open && textareaRef.current) {
    const ta = textareaRef.current;
    const caret = getCaretCoordinates(ta, mention.start);
    const estHeight = Math.min(mentionItems.length * MENTION_ROW_PX + 8, MENTION_MENU_MAX_PX);
    const rect = ta.getBoundingClientRect();
    const caretViewportY = rect.top + caret.top - ta.scrollTop;
    const left = rect.left + caret.left - ta.scrollLeft;
    const flipUp = caretViewportY + caret.height + estHeight > window.innerHeight;
    menuCoords = { top: flipUp ? caretViewportY - estHeight : caretViewportY + caret.height, left };
  }

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={textareaRef}
        className="input textarea"
        style={{ minHeight: '120px', overflow: 'hidden', resize: 'vertical', width: '100%' }}
        value={shotEdits.prompt}
        onChange={(event) => {
          onShotEditsChange({ ...shotEdits, prompt: event.target.value });
          autoResize(event.target);
          mention.handleSelectionChange();
        }}
        onKeyDown={mention.handleKeyDown}
        onClick={mention.handleCaretMove}
        onSelect={mention.handleCaretMove}
        onBlur={mention.close}
      />
      {mention.open && menuCoords && (
        <AssetMentionMenu
          items={mentionItems}
          selectedId={mention.selectedId}
          coords={menuCoords}
          onPick={(id) => mention.confirm(id)}
          onHover={mention.setSelectedId}
        />
      )}
    </div>
  );
}
